import React, { useState } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import {
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'

import { Status } from '~/constants/data-field'
import { isSoja } from '~/enums/enum'
import { useGrowCropsStore } from '~/store/variety/growcrops'
import { VorietyTransterInputProps } from '~/types/grow-crops-types'
import { apiFieldErrors } from '~/utils/api-field-errors'

import { ModalPostTechnologyGenetic } from '../modal-post'
import { useStyles } from './styles'

interface Props {
  nameField: string
  label: string
}

interface GrowCropsFormsProps {
  isEditing?: boolean
}

const GrowCropsForms: React.FC<GrowCropsFormsProps> = ({ isEditing = false }) => {
  const [title, setTitle] = useState<Props>({} as Props)
  const classes = useStyles()

  const cultureList = useGrowCropsStore((state) => state.cultureList)
  const geneticList = useGrowCropsStore((state) => state.geneticList)
  const tecnologyList = useGrowCropsStore((state) => state.tecnologyList)
  const modalPostController = useGrowCropsStore((state) => state.modalPostController)
  const messageError = useGrowCropsStore((state) => state.messageError)

  const apiErrors = apiFieldErrors(messageError)

  const {
    control,
    formState: { errors },
  } = useFormContext<VorietyTransterInputProps>()

  const selectedCultureId = useWatch({ control, name: 'cultura_id' })
  const isSojaSelected = isSoja(selectedCultureId)

  const handlePopperForms = (label: string, nameField: string) => {
    modalPostController(true)
    setTitle({
      label,
      nameField,
    })
  }

  return (
    <>
      <Grid container spacing={3} className={classes.modalStyle}>
        <Grid item sm={6}>
          <Controller
            name="cultura_id"
            control={control}
            render={({ field }) => (
              <FormControl variant="outlined" className={classes.selectInput}>
                <InputLabel id="cultura_label" disabled={isEditing}>
                  {isEditing ? 'Cultura' : 'Cultura*'}
                </InputLabel>
                <Select
                  {...field}
                  labelId="cultura_label"
                  id="textfield-cultura"
                  label={isEditing ? 'Cultura' : 'Cultura*'}
                  autoWidth
                  required={!isEditing}
                  disabled={isEditing}
                  error={!!errors?.cultura_id || !!apiErrors.cultura_id}
                  value={field.value || ''}
                >
                  {cultureList.map((culture) => (
                    <MenuItem key={culture.id} value={culture.id}>
                      {culture.nome}
                    </MenuItem>
                  ))}
                </Select>
                {(errors.cultura_id?.message || apiErrors.cultura_id) && (
                  <FormHelperText className={classes.helperText}>
                    {errors.cultura_id?.message ?? apiErrors.cultura_id}
                  </FormHelperText>
                )}
                {!errors.cultura_id?.message && !apiErrors.cultura_id && isEditing && (
                  <FormHelperText>A cultura não pode ser alterada após o cadastro</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>

        <Grid item sm={6}>
          <Controller
            name="nome"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                required
                id="textfield-nome"
                variant="outlined"
                label="Nome"
                placeholder="Nome"
                className={classes.selectInput}
                error={!!errors?.nome || !!apiErrors.nome}
                FormHelperTextProps={{
                  className: classes.helperText,
                }}
                helperText={errors.nome?.message ?? apiErrors.nome}
              />
            )}
          />
        </Grid>

        <Grid item sm={6}>
          <Paper className={classes.root}>
            <IconButton
              color="primary"
              className={classes.iconButton}
              onClick={() => handlePopperForms('Genética', 'genetica')}
            >
              <AddIcon />
            </IconButton>
            <Controller
              name="genetica_id"
              control={control}
              render={({ field }) => (
                <FormControl variant="outlined" className={classes.selectInput}>
                  <InputLabel id="geneticaLabel">Genética*</InputLabel>
                  <Select
                    labelId="geneticaLabel"
                    id="textfield-genetica"
                    label="Genética*"
                    autoWidth
                    required
                    error={!!errors?.genetica_id || !!apiErrors.genetica_id}
                    {...field}
                  >
                    {geneticList.map((Option) => (
                      <MenuItem key={Option.id} value={Option.id}>
                        {Option.nome}
                      </MenuItem>
                    ))}
                  </Select>
                  {(errors.genetica_id?.message || apiErrors.genetica_id) && (
                    <FormHelperText className={classes.helperText}>
                      {errors.genetica_id?.message ?? apiErrors.genetica_id}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Paper>
        </Grid>

        <Grid item sm={6}>
          <Paper className={classes.root}>
            <IconButton
              color="primary"
              className={classes.iconButton}
              onClick={() => handlePopperForms('Tecnologia', 'tecnologia')}
            >
              <AddIcon />
            </IconButton>
            <Controller
              name="tecnologia"
              control={control}
              render={({ field }) => (
                <FormControl variant="outlined" className={classes.selectInput}>
                  <InputLabel id="tecnologiaLabel">Tecnologia*</InputLabel>
                  <Select
                    labelId="tecnologiaLabel"
                    id="textfield-tecnologia"
                    label="Tecnologia*"
                    autoWidth
                    required
                    error={!!errors?.tecnologia || !!apiErrors.tecnologia}
                    {...field}
                  >
                    {tecnologyList.map((Option) => (
                      <MenuItem key={Option.id} value={Option.id}>
                        {Option.nome}
                      </MenuItem>
                    ))}
                  </Select>
                  {(errors.tecnologia?.message || apiErrors.tecnologia) && (
                    <FormHelperText className={classes.helperText}>
                      {errors.tecnologia?.message ?? apiErrors.tecnologia}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Paper>
        </Grid>

        <Grid item sm={6}>
          <Controller
            name="grupo_maturacao"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                id="textfield-maturacao"
                variant="outlined"
                label="Grupo de Maturação"
                disabled={!isSojaSelected}
                required={isSojaSelected}
                placeholder="Grupo de Maturação"
                className={classes.selectInput}
                error={!!errors?.grupo_maturacao || !!apiErrors.grupo_maturacao}
                FormHelperTextProps={{
                  className: classes.helperText,
                }}
                helperText={errors.grupo_maturacao?.message ?? apiErrors.grupo_maturacao}
              />
            )}
          />
        </Grid>

        {isSojaSelected && (
          <Grid item sm={6}>
            <Controller
              name="sts"
              control={control}
              render={({ field }) => (
                <FormControl variant="outlined" className={classes.selectInput}>
                  <InputLabel id="stsLabel">Tecnologia STS*</InputLabel>
                  <Select
                    labelId="stsLabel"
                    id="textfield-sts"
                    label="Tecnologia STS*"
                    autoWidth
                    required
                    error={!!errors?.sts || !!apiErrors.sts}
                    {...field}
                    value={field.value ?? ''}
                  >
                    <MenuItem value="1">Sim</MenuItem>
                    <MenuItem value="0">Não</MenuItem>
                    <MenuItem value="2">Sem informação</MenuItem>
                  </Select>
                  {(errors.sts?.message || apiErrors.sts) && (
                    <FormHelperText className={classes.helperText}>
                      {errors.sts?.message ?? apiErrors.sts}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>
        )}

        <Grid item sm={6}>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <FormControl variant="outlined" className={classes.selectInput}>
                <InputLabel id="statusLabel">Status*</InputLabel>
                <Select
                  labelId="statusLabel"
                  id="textfield-status"
                  label="Status*"
                  autoWidth
                  required
                  error={!!errors?.status || !!apiErrors.status}
                  {...field}
                >
                  {Status.map((Option) => (
                    <MenuItem key={Option.statusValue} value={Option.statusValue}>
                      {Option.statusName}
                    </MenuItem>
                  ))}
                </Select>
                {(errors.status?.message || apiErrors.status) && (
                  <FormHelperText className={classes.helperText}>
                    {errors.status?.message ?? apiErrors.status}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>
      </Grid>
      <ModalPostTechnologyGenetic label={title.label} nameField={title.nameField} />
    </>
  )
}
export default GrowCropsForms
